import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/MentionInformation.css';

/**
 * Mention d'information sur le traitement des données personnelles
 * (RGPD, art. 13) — version validée par la déléguée à la protection des
 * données de l'Université Paris Cité, 4 septembre 2026.
 *
 * Les deux langues sont présentées ensemble, et les deux pages complètes
 * restent accessibles en permanence : l'interface est en anglais alors que
 * le responsable de traitement est un établissement public français et que
 * la majorité des doctorants sont francophones. Il n'y a pas de bascule de
 * langue dans l'application, on ne peut donc pas afficher l'une ou l'autre.
 *
 * Toute modification de ces textes doit repasser par la DPO.
 */

export const CONTACT_DPO = 'dpo@u-paris.fr';

/** Mention courte bilingue, affichée en pied des formulaires. */
const MentionInformation: React.FC = () => (
  <section
    className="mention-rgpd"
    aria-label="Information on the processing of personal data / Information sur le traitement des données personnelles"
  >
    <p lang="en">
      The data collected in this form is processed by Université Paris Cité as
      part of the individual monitoring committee provided for by the French
      ministerial order of 25 May 2016 on doctoral studies. It is intended for
      the BioSPC doctoral school and the members of your committee. You have
      rights over your data.
    </p>

    <p lang="fr">
      Les données recueillies dans ce formulaire sont traitées par l'Université
      Paris Cité, dans le cadre du comité de suivi individuel prévu par l'arrêté
      du 25 mai 2016 relatif au doctorat. Elles sont destinées à l'école
      doctorale BioSPC et aux membres de votre comité. Vous disposez de droits
      sur vos données.
    </p>

    <p className="mention-rgpd-liens">
      <Link to="/privacy-notice" target="_blank" rel="noopener noreferrer">
        Privacy notice (EN)
      </Link>
      <span aria-hidden="true"> | </span>
      <Link to="/donnees-personnelles" target="_blank" rel="noopener noreferrer">
        Mentions d'information (FR)
      </Link>
    </p>
  </section>
);

/** Bandeau permettant de passer d'une version linguistique à l'autre. */
const SelecteurLangue: React.FC<{ courante: 'fr' | 'en' }> = ({ courante }) => (
  <p className="mention-rgpd-bascule">
    {courante === 'fr' ? (
      <Link to="/privacy-notice">This page in English</Link>
    ) : (
      <Link to="/donnees-personnelles">Cette page en français</Link>
    )}
  </p>
);

/** Page complète — version française. */
export const PageMentionInformation: React.FC = () => (
  <div className="mention-rgpd-page" lang="fr">
    <SelecteurLangue courante="fr" />

    <h1>Information sur le traitement des données personnelles</h1>
    <p className="mention-rgpd-sous-titre">
      Application des comités de suivi individuel, école doctorale BioSPC (ED 562)
    </p>

    <h2>Responsable de traitement</h2>
    <p>
      L'Université Paris Cité, représentée par son Président. Le traitement est
      mis en œuvre par l'école doctorale BioSPC.
    </p>

    <h2>Finalités</h2>
    <p>
      Organiser le comité de suivi individuel de chaque doctorant : dépôt du
      rapport annuel, recueil de l'appréciation du comité, transmission à la
      direction de l'école doctorale en vue de l'avis de réinscription. Assurer
      le suivi du parcours doctoral et de la qualité de l'encadrement au sein de
      l'école doctorale.
    </p>

    <h2>Base légale</h2>
    <p>
      Exécution d'une mission d'intérêt public confiée à l'université (article
      6.1.e du RGPD), en application de l'arrêté du 25 mai 2016 fixant le cadre
      national de la formation et les modalités conduisant à la délivrance du
      diplôme national de doctorat.
    </p>

    <h2>Données traitées</h2>
    <p>
      Pour le doctorant : identité, adresse électronique, identifiants (ORCID,
      identifiant étudiant), unité et équipe de recherche, intitulé de la thèse,
      financement, dates de la thèse, rapport scientifique, parcours de
      formation, auto-évaluation des compétences.
    </p>
    <p>
      Pour les encadrants et membres du comité : nom, adresse électronique,
      rattachement, appréciation rédigée. Une partie de l'appréciation du comité
      est réservée à la direction de l'école doctorale et n'est communiquée ni au
      doctorant ni aux encadrants.
    </p>

    <h2>Destinataires</h2>
    <p>
      Les membres du comité de suivi du doctorant concerné, la direction de
      l'école doctorale et les directeurs de département pour la validation. Le
      compte rendu validé est adressé au doctorant et à ses encadrants. Les
      prestataires assurant l'hébergement de l'application, de sa base de données
      et de la base de gestion de l'école doctorale interviennent en qualité de
      sous-traitants, sur des infrastructures situées dans l'Union européenne, et
      n'accèdent pas au contenu des dossiers.
    </p>

    <h2>Durée de conservation</h2>
    <p>
      Les données sont conservées dans l'application le temps de la campagne
      annuelle, puis, après validation, dans la base de gestion de l'école
      doctorale pendant dix ans après la fin de la thèse.
    </p>

    <h2>Vos droits</h2>
    <p>
      Vous pouvez accéder et obtenir copie des données vous concernant, vous
      opposer au traitement de ces données, les faire rectifier ou les faire
      effacer. Vous disposez également d'un droit à la limitation du traitement
      de vos données. Pour exercer ces droits ou pour toute question sur le
      traitement de vos données, vous pouvez contacter la déléguée à la
      protection des données (DPD) de l'université par courriel à{' '}
      <a href={`mailto:${CONTACT_DPO}`}>{CONTACT_DPO}</a> (avec copie de votre
      pièce d'identité en cas d'exercice de vos droits, sauf si les éléments
      communiqués dans votre demande permettent de vous identifier de façon
      certaine) ou par courrier postal à :
    </p>
    <p className="mention-rgpd-adresse">
      Université Paris Cité
      <br />
      À l'attention de la déléguée à la protection des données
      <br />
      85 boulevard Saint-Germain
      <br />
      75006 PARIS
    </p>
    <p>
      Si vous estimez, après nous avoir contactés, que vos droits « Informatique
      et Libertés » ne sont pas respectés, vous pouvez adresser une réclamation à
      la{' '}
      <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
        CNIL
      </a>
      .
    </p>

    <h2>Sécurité</h2>
    <p>
      L'université met en œuvre des mesures techniques et organisationnelles
      appropriées pour protéger vos données contre toute perte, destruction,
      altération, accès ou divulgation non autorisée.
    </p>
  </div>
);

/** Page complète — version anglaise. */
export const PrivacyNoticePage: React.FC = () => (
  <div className="mention-rgpd-page" lang="en">
    <SelecteurLangue courante="en" />

    <h1>Information on the processing of personal data</h1>
    <p className="mention-rgpd-sous-titre">
      Individual monitoring committee application, BioSPC doctoral school (ED 562)
    </p>

    <h2>Data controller</h2>
    <p>
      Université Paris Cité, represented by its President. The processing is
      carried out by the BioSPC doctoral school.
    </p>

    <h2>Purposes</h2>
    <p>
      To organise each doctoral candidate's individual monitoring committee:
      submission of the annual report, collection of the committee's assessment,
      and transmission to the management of the doctoral school for the purposes
      of the re-registration decision. To monitor the doctoral pathway and the
      quality of supervision within the doctoral school.
    </p>

    <h2>Legal basis</h2>
    <p>
      Performance of a task carried out in the public interest vested in the
      university (Article 6(1)(e) GDPR), pursuant to the ministerial order of
      25 May 2016 setting out the national framework for training and the
      requirements for the award of the national doctoral degree.
    </p>

    <h2>Data processed</h2>
    <p>
      For the doctoral candidate: identity, email address, identifiers (ORCID,
      student number), research unit and team, thesis title, funding, thesis
      dates, scientific report, training record, self-assessment of competencies.
    </p>
    <p>
      For supervisors and committee members: name, email address, affiliation,
      written assessment. Part of the committee's assessment is reserved for the
      management of the doctoral school and is disclosed neither to the doctoral
      candidate nor to the supervisors.
    </p>

    <h2>Recipients</h2>
    <p>
      The members of the monitoring committee of the doctoral candidate
      concerned, the management of the doctoral school, and the department
      directors for validation purposes. The validated report is sent to the
      doctoral candidate and their supervisors. The providers hosting the
      application, its database and the doctoral school's management database act
      as processors, on infrastructure located within the European Union, and do
      not access the content of the files.
    </p>

    <h2>Retention period</h2>
    <p>
      Data is retained in the application for the duration of the annual
      campaign, then, once validated, in the doctoral school's management
      database for ten years following the end of the thesis.
    </p>

    <h2>Your rights</h2>
    <p>
      You may access and obtain a copy of the data concerning you, object to its
      processing, and have it rectified or erased. You also have the right to
      restriction of processing. To exercise these rights, or for any question
      regarding the processing of your data, you may contact the university's
      Data Protection Officer by email at{' '}
      <a href={`mailto:${CONTACT_DPO}`}>{CONTACT_DPO}</a> (enclosing a copy of
      your identity document when exercising your rights, unless the information
      provided in your request allows you to be identified with certainty) or by
      post at:
    </p>
    <p className="mention-rgpd-adresse">
      Université Paris Cité
      <br />
      For the attention of the Data Protection Officer
      <br />
      85 boulevard Saint-Germain
      <br />
      75006 Paris, France
    </p>
    <p>
      If, after contacting us, you consider that your data protection rights have
      not been respected, you may lodge a complaint with the{' '}
      <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
        CNIL
      </a>
      , the French data protection authority.
    </p>

    <h2>Security</h2>
    <p>
      The university implements appropriate technical and organisational measures
      to protect your data against loss, destruction, alteration, and
      unauthorised access or disclosure.
    </p>
  </div>
);

export default MentionInformation;
